import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterBar, FilterConfig } from '@/components/ui/FilterBar'
import {
  BookOpen,
  Clock,
  Building2,
  Plus,
  Play,
  Layers,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useCourses } from '@/hooks/useCourses'
import { PageSkeleton } from '@/components/LoadingSkeleton'
import { CourseModel } from '@/models/course.model'
import { paths } from '@/routes/path'
import throttle from 'lodash/throttle'
import { UserRole } from '@/models/user.model'

const SEARCH_THROTTLE_MS = 400

// Memoized course card component
const CourseCard = React.memo(({ course, canManage }: { course: CourseModel; canManage: boolean }) => {
  const durationLabel = course.durationLabel ?? (course.credits ? `${course.credits} Credits` : undefined)
  const status = course.activityStatus
  const statusStyles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    upcoming: 'bg-blue-100 text-blue-800',
    completed: 'bg-purple-100 text-purple-800',
    inactive: 'bg-gray-100 text-gray-700',
  }
  const statusLabel: Record<string, string> = {
    active: 'Active',
    upcoming: 'Upcoming',
    completed: 'Completed',
    inactive: 'Inactive',
  }

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md bg-white">
      <div className="aspect-video bg-gradient-to-br from-red-100 to-pink-200 flex items-center justify-center overflow-hidden">
        <div className="text-center text-red-600">
          <BookOpen className="h-12 w-12 mx-auto mb-2" />
          <p className="text-sm font-medium">Course Library</p>
        </div>
      </div>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <Link to={paths.app.courses.detail.build(course.id)}>
            <CardTitle className="text-lg hover:text-red-600 transition-colors cursor-pointer font-semibold">
              {course.title}
            </CardTitle>
          </Link>
          <Badge 
            className={statusStyles[status] ?? statusStyles.active}
          >
            {statusLabel[status] ?? status}
          </Badge>
        </div>
        <CardDescription className="line-clamp-2 text-gray-600">
          {course.description || 'No description available for this course yet.'}
        </CardDescription>
        <div className="flex items-center gap-2 mt-2">
          <Building2 className="h-4 w-4 text-gray-500" />
          <span className="text-sm text-gray-600">
            {course.departmentName || course.programmeTitle || 'Unassigned department'}
          </span>
        </div>
        {course.code && (
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium tracking-wide text-xs uppercase">
              {course.code}
            </span>
          </div>
        )}
        {durationLabel && (
          <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4" />
            <span>{durationLabel}</span>
          </div>
        )}
        {course.semester && (
          <div className="mt-1 text-xs text-gray-500">
            Semester: {course.semester} • Level: {course.level ?? 'N/A'} • Modules: {course.modules.length ?? 'N/A'}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="pt-2 space-y-2">
          <Link to={paths.app.courses.detail.build(course.id)}>
            <Button className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium">
              <Play className="h-4 w-4 mr-2" />
              View Course
            </Button>
          </Link>
          {canManage && (
            <div>
              <Link to={paths.app.courses.manage.build(course.id)}>
                <Button variant="outline" className="w-full border-primary-200 text-primary-600 hover:bg-primary-50">
                  Manage Course
                </Button>
              </Link>
              <Link to={paths.app.courses.modules.root.build(course.id)}>
                <Button variant="ghost" className="w-full text-primary-600 hover:bg-primary-50">
                  <Layers className="h-4 w-4 mr-2" />
                  Modules
                </Button>
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
})

CourseCard.displayName = 'CourseCard'

export function OptimizedCoursesPage() {
  const [filters, setFilters] = useState<{ search?: string; status?: string; department?: string; level?: string; semester?: string }>({})
  const [throttledSearch, setThrottledSearch] = useState<string | undefined>(undefined)
  const navigate = useNavigate()

  const { user } = useAuth()
  const throttledUpdate = useMemo(
    () =>
      throttle(
        (value?: string) => {
          setThrottledSearch(value)
        },
        SEARCH_THROTTLE_MS,
        { leading: false, trailing: true },
      ),
    [],
  )

  useEffect(() => {
    return () => {
      throttledUpdate.cancel()
    }
  }, [throttledUpdate])

  const handleFiltersChange = useCallback(
    (nextFilters: { search?: string; status?: string; department?: string; level?: string; semester?: string }) => {
      setFilters(nextFilters)
      const normalizedSearch = nextFilters.search?.trim() || undefined
      if (normalizedSearch === undefined) {
        throttledUpdate.cancel()
        setThrottledSearch(undefined)
      } else {
        throttledUpdate(normalizedSearch)
      }
    },
    [throttledUpdate],
  )

  const { data: coursesResponse, isLoading, error } = useCourses({
    search: throttledSearch,
    level: filters.level || undefined,
    semester: filters.semester || undefined,
  })
  const courses = coursesResponse?.data ?? []
  const isInitialLoading = isLoading && !coursesResponse

  // Memoized filtered courses
  const filteredCourses = useMemo(() => {
    if (!courses) return []
    
    const searchTerm = filters.search?.trim().toLowerCase()

    return courses.filter(course => {
      const matchesSearch = !searchTerm ||
        course.title.toLowerCase().includes(searchTerm) ||
        (course.description ?? '').toLowerCase().includes(searchTerm) ||
        (course.code ?? '').toLowerCase().includes(searchTerm)

      const matchesStatus = !filters.status || course.activityStatus === filters.status
      const matchesDepartment =
        !filters.department ||
        course.departmentName === filters.department ||
        course.programmeTitle === filters.department
      const matchesLevel = !filters.level || `${course.level}` === `${filters.level}`
      const matchesSemester = !filters.semester || `${course.semester}` === `${filters.semester}`

      return matchesSearch && matchesStatus && matchesDepartment && matchesLevel && matchesSemester
    })
  }, [courses, filters])

  // Memoized filter configuration
  const filterConfig: FilterConfig[] = useMemo(() => {
    const departmentOptions = Array.from(
      new Set(
        courses
          .map((course) => course.departmentName || course.programmeTitle || null)
          .filter((value): value is string => Boolean(value)),
      ),
    ).map((department) => ({
      value: department,
      label: department,
      count: courses.filter(
        (course) => course.departmentName === department || course.programmeTitle === department,
      ).length,
    }))

    const statusCounts = courses.reduce<Record<string, number>>((acc, course) => {
      const status = course.activityStatus
      acc[status] = (acc[status] ?? 0) + 1
      return acc
    }, {})

    const levelOptions = [
      { value: '1', label: 'Year 1', count: courses.filter((course) => `${course.level}` === '1').length },
      { value: '2', label: 'Year 2', count: courses.filter((course) => `${course.level}` === '2').length },
    ]

    const semesterOptions = [
      { value: '1', label: 'Semester 1', count: courses.filter((course) => `${course.semester}` === '1').length },
      { value: '2', label: 'Semester 2', count: courses.filter((course) => `${course.semester}` === '2').length },
    ]

    return [
      {
        key: 'search',
        type: 'search',
        label: 'Search',
        placeholder: 'Search courses...',
      },
      {
        key: 'status',
        type: 'select',
        label: 'Status',
        options: [
          { value: '', label: 'All status', count: courses.length },
          { value: 'active', label: 'Active', count: statusCounts.active ?? 0 },
          { value: 'upcoming', label: 'Upcoming', count: statusCounts.upcoming ?? 0 },
          { value: 'completed', label: 'Completed', count: statusCounts.completed ?? 0 },
          { value: 'inactive', label: 'Inactive', count: statusCounts.inactive ?? 0 },
        ],
      },
      {
        key: 'level',
        type: 'select',
        label: 'Year',
        options: [{ value: '', label: 'All years', count: courses.length }, ...levelOptions],
      },
      {
        key: 'semester',
        type: 'select',
        label: 'Semester',
        options: [{ value: '', label: 'All semesters', count: courses.length }, ...semesterOptions],
      },
      {
        key: 'department',
        type: 'select',
        label: 'Department',
        options: [{ value: '', label: 'All departments', count: courses.length }, ...departmentOptions],
      },
    ]
  }, [courses])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Courses</h2>
          <p className="text-gray-600">Please try refreshing the page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 bg-gradient-to-br from-purple-50 to-pink-50 min-h-screen p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
          <p className="text-gray-600 mt-2">
            Browse and manage your courses
          </p>
        </div>
        {/* Only show course creation buttons for instructors and admins */}
        {user && (user.role === UserRole.INSTRUCTOR || user.role === UserRole.ADMIN) && (
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate(paths.app.courses.create.absolute)} 
              className="bg-primary-600 hover:bg-primary-700 text-white font-medium"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Course
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filterConfig}
        onFiltersChange={handleFiltersChange}
        searchPlaceholder="Search courses by title or instructor..."
      />

      {
        isInitialLoading ? <PageSkeleton content />: 
          filteredCourses.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredCourses.map((course) => (
                <CourseCard key={course.id} course={course} canManage={Boolean(user && (user.role === 'admin' || user.role === 'instructor'))} />
              ))}
            </div>
            :!isLoading &&
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
              <p className="text-gray-600">
                {filters.search ? 'Try adjusting your search criteria.' : 'No courses have been created yet.'}
              </p>
            </div>
      }
    </div>
  )
}
